import { useState, useEffect, useRef } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { useLanguage } from "@/hooks/use-language";
import { SUPPORTED_LANGUAGES } from "@/contexts/language-context";
import { campaignTranslations } from "@/constants/campaign-translations";
import { TURNSTILE_SITE_KEY } from "@/constants/config";

/* Campaign-scoped translation resolver. Uses the global language code so the
   switcher stays in sync with the rest of the site. Returns the key itself
   when the path is missing, which makes typos loud during development. */
const useCmpT = () => {
  const { lang } = useLanguage();
  const dict = campaignTranslations[lang] || campaignTranslations.en;
  return (path) => {
    const parts = path.split(".");
    let cur = dict;
    for (const p of parts) cur = cur?.[p];
    if (cur === undefined) {
      let fb = campaignTranslations.en;
      for (const p of parts) fb = fb?.[p];
      return fb ?? path;
    }
    return cur;
  };
};

/* ── Campaign-scoped CSS injected on mount, removed on unmount ── */
const CAMPAIGN_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Press+Start+2P&family=JetBrains+Mono:wght@400;500;700&display=swap');

.cmp-root {
  font-family: "Plus Jakarta Sans", Inter, system-ui, sans-serif;
  background: #06050f;
  color: #f5f3ff;
  overflow-x: hidden;
  font-size: 14px;
}
@media (max-width: 640px) { .cmp-root { font-size: 13px; } }

.cmp-root .page-bg {
  position: fixed; inset: 0; z-index: 0; pointer-events: none;
  background:
    radial-gradient(1200px 700px at 80% -10%, rgba(255,0,127,0.22), transparent 60%),
    radial-gradient(900px 600px at 10% 20%, rgba(0,240,255,0.14), transparent 60%),
    radial-gradient(900px 900px at 50% 110%, rgba(168,85,247,0.18), transparent 60%),
    linear-gradient(180deg, #0b0a1a 0%, #07061a 50%, #050418 100%);
}
.cmp-root .page-bg::after {
  content: ""; position: absolute; inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
  background-size: 56px 56px;
  mask-image: radial-gradient(ellipse 90% 70% at 50% 30%, black 40%, transparent 80%);
}

.cmp-root .font-pixel { font-family: "Press Start 2P", monospace; letter-spacing: 0.02em; }
.cmp-root .font-mono-cmp { font-family: "JetBrains Mono", monospace; }

.cmp-root .h-neon {
  background: linear-gradient(180deg, #fff 0%, #ffd0e4 55%, #ff007f 100%);
  -webkit-background-clip: text; background-clip: text; color: transparent;
  filter: drop-shadow(0 2px 0 rgba(0,0,0,0.4));
}
.cmp-root .text-stroke { -webkit-text-stroke: 1px rgba(255,0,127,0.4); }

.cmp-root .clay-card {
  background: linear-gradient(180deg, rgba(32,24,70,0.8), rgba(14,10,34,0.85));
  border: 1px solid rgba(255,255,255,0.06);
  box-shadow:
    0 24px 60px -20px rgba(0,0,0,0.9),
    inset 0 1px 0 rgba(255,255,255,0.08),
    inset 0 -2px 0 rgba(0,0,0,0.45);
  border-radius: 20px;
}
.cmp-root .clay-button {
  background: linear-gradient(180deg, #ff1a8c 0%, #c4005e 100%);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.18);
  box-shadow: 0 10px 24px -8px rgba(255,0,127,0.65), inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -3px 0 rgba(0,0,0,0.35);
  border-radius: 999px; font-weight: 700; letter-spacing: .02em;
  transition: transform .15s ease, box-shadow .15s ease;
}
.cmp-root .clay-button:hover { transform: translateY(-1px); box-shadow: 0 16px 30px -10px rgba(255,0,127,0.85), inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -3px 0 rgba(0,0,0,0.35); }
.cmp-root .clay-button:active { transform: translateY(1px); }

.cmp-root .glass-card, .cmp-root .glass-panel {
  background: rgba(22, 16, 48, 0.48);
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  border-radius: 20px;
}
@media (max-width: 768px) {
  .cmp-root .glass-card, .cmp-root .glass-panel {
    backdrop-filter: none; -webkit-backdrop-filter: none; background: rgba(22, 16, 48, 0.78);
  }
}

.cmp-root .retro-button {
  display: inline-flex; align-items: center; gap: .625rem;
  padding: 14px 22px; border-radius: 10px;
  background: #0b0a1a; color: #fff;
  border: 2px solid #ff007f;
  box-shadow: 6px 6px 0 0 #00f0ff;
  font-family: "Press Start 2P", monospace; font-size: 12px; letter-spacing: .06em;
  transition: transform .1s ease, box-shadow .1s ease;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
}
.cmp-root .retro-button:hover { transform: translate(-2px,-2px); box-shadow: 8px 8px 0 0 #00f0ff; }
.cmp-root .retro-button:active { transform: translate(2px,2px); box-shadow: 2px 2px 0 0 #00f0ff; }
.cmp-root .retro-button.alt { border-color: #00f0ff; box-shadow: 6px 6px 0 0 #ff007f; }
.cmp-root .retro-button.alt:hover { box-shadow: 8px 8px 0 0 #ff007f; }
.cmp-root .retro-icon-btn {
  width: 44px; height: 44px; display: inline-flex; align-items: center; justify-content: center;
  background: #0b0a1a; border: 2px solid #ff007f; border-radius: 10px; color: #fff;
  box-shadow: 4px 4px 0 0 #00f0ff; flex-shrink: 0;
}

.cmp-root .chip {
  display: inline-flex; align-items: center; gap: .5rem; padding: 6px 12px; border-radius: 999px;
  background: rgba(0,240,255,0.08); border: 1px solid rgba(0,240,255,0.4); color: #c6fbff;
  font-family: "JetBrains Mono", monospace; font-size: 11px; letter-spacing: .08em; text-transform: uppercase;
}
.cmp-root .chip.pink { background: rgba(255,0,127,0.1); border-color: rgba(255,0,127,0.45); color: #ffc2df; }

.cmp-root .hrule { height: 1px; background: linear-gradient(90deg, transparent, rgba(255,0,127,0.6), rgba(0,240,255,0.6), transparent); }

.cmp-root .flame-glow { filter: drop-shadow(0 0 16px rgba(255,120,40,0.7)) drop-shadow(0 0 30px rgba(255,0,127,0.6)); }

.cmp-root .sparkle {
  position: absolute; width: 10px; height: 10px; background: #fff; transform: rotate(45deg);
  box-shadow: 0 0 16px 3px rgba(0,240,255,0.8);
}

.cmp-root .scanlines::after {
  content: ""; position: absolute; inset: 0; pointer-events: none; opacity: .15;
  background: repeating-linear-gradient(0deg, rgba(255,255,255,0.12) 0 1px, transparent 1px 3px);
}

.cmp-root .hero-stage { position: relative; }
.cmp-root .hero-ring {
  position: absolute; inset: 0; border-radius: 50%;
  background: conic-gradient(from 0deg, #ff007f, #a855f7, #00f0ff, #ff007f);
  filter: blur(20px); opacity: .55;
}

.cmp-root .marquee {
  display: flex; overflow: hidden; gap: 3rem;
  mask-image: linear-gradient(90deg, transparent, black 10%, black 90%, transparent);
}
.cmp-root .marquee-track {
  display: flex; gap: 3rem; animation: cmp-marquee 28s linear infinite; white-space: nowrap;
}
@keyframes cmp-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }

.cmp-root .step-num {
  font-family: "Press Start 2P", monospace; font-size: 28px;
  background: linear-gradient(180deg, #00f0ff 0%, #ff007f 100%);
  -webkit-background-clip: text; background-clip: text; color: transparent;
}

.cmp-root .field {
  width: 100%; background: rgba(10,8,24,0.75); border: 1px solid rgba(255,255,255,0.08);
  padding: 14px 16px; border-radius: 12px; color: #fff; outline: none;
  font-family: "JetBrains Mono", monospace; font-size: 13px;
  box-shadow: inset 0 2px 0 rgba(0,0,0,0.45), inset 0 -1px 0 rgba(255,255,255,0.04);
  transition: border-color .15s ease, box-shadow .15s ease;
}
.cmp-root .field:focus { border-color: #ff007f; box-shadow: 0 0 0 4px rgba(255,0,127,0.15), inset 0 2px 0 rgba(0,0,0,0.45); }
.cmp-root .field::placeholder { color: #6b6592; }

.cmp-root .fire-bar {
  background: linear-gradient(90deg, #ff5a1f, #ff007f 50%, #00f0ff);
  height: 8px; border-radius: 999px; box-shadow: 0 0 16px rgba(255,0,127,0.5);
}

.cmp-root section[id] { scroll-margin-top: 80px; }

/* ── Hero background video (tablet + mobile only) ── */
.cmp-root .hero-bg-video-wrap { display: none; }
@media (max-width: 1024px) {
  .cmp-root .hero-bg-video-wrap {
    display: block;
    position: absolute; inset: 0; z-index: 0; overflow: hidden;
    pointer-events: none;
  }
  .cmp-root .hero-bg-video-wrap video {
    position: absolute; inset: 0;
    width: 100%; height: 100%; object-fit: cover;
  }
  .cmp-root .hero-bg-video-wrap .hero-bg-video-overlay {
    position: absolute; inset: 0;
    background:
      linear-gradient(180deg, rgba(6,4,24,0.55) 0%, rgba(6,4,24,0.75) 55%, rgba(6,4,24,0.92) 100%),
      radial-gradient(900px 600px at 20% 30%, rgba(255,0,127,0.35), transparent 60%),
      radial-gradient(800px 500px at 80% 70%, rgba(0,240,255,0.22), transparent 60%);
  }
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .cmp-root .nav-links-desktop { display: none !important; }
}

@media (max-width: 1024px) {
  .cmp-root .hero-deco-sun  { display: none !important; }
  .cmp-root .hero-deco-bread { display: none !important; }
  /* Hide the phone column entirely — the background video takes over. */
  .cmp-root .hero-col-phone { display: none !important; }
  /* Hide the static hero-bg image since the video is the background now. */
  .cmp-root .hero-static-bg { display: none !important; }
  .cmp-root .hero-grid { grid-template-columns: 1fr !important; }
  .cmp-root .hero-col-main { grid-column: 1 !important; }
}

@media (max-width: 768px) {
  .cmp-root .hero-stats { grid-template-columns: repeat(3,1fr) !important; }
}
@media (max-width: 480px) {
  .cmp-root .hero-stats { grid-template-columns: 1fr 1fr !important; }
}

@media (max-width: 768px) {
  .cmp-root .step-stagger { transform: none !important; }
}

@media (max-width: 480px) {
  .cmp-root .reward-flex { flex-direction: column !important; align-items: center !important; text-align: center !important; }
}

@media (max-width: 640px) {
  .cmp-root .submit-card { padding: 20px 16px !important; }
  .cmp-root .final-cta-panel { padding: 32px 20px !important; }
}

/* Tighten vertical rhythm on mobile — the default section padding
   (5rem / 7rem, 6rem / 8rem on final CTA) leaves huge gaps on small screens. */
@media (max-width: 768px) {
  .cmp-root section { padding-top: 2.5rem !important; padding-bottom: 3rem !important; }
  .cmp-root #top { padding-top: 0 !important; padding-bottom: 0 !important; }
}
@media (max-width: 480px) {
  .cmp-root section { padding-top: 2rem !important; padding-bottom: 2.25rem !important; }
}

/* Vietnamese fallback: Press Start 2P has no Vietnamese diacritic glyphs,
   so VN text on retro buttons renders with broken/missing characters.
   Swap to JetBrains Mono (bold) when lang=vi — still mono/retro, fully VN-capable. */
.cmp-root.cmp-lang-vi .retro-button {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.08em;
}

/* Animations */
@keyframes cmp-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
@keyframes cmp-glow {
  0%,100% { filter: drop-shadow(0 0 12px rgba(255,0,127,0.6)); }
  50% { filter: drop-shadow(0 0 28px rgba(255,0,127,0.95)); }
}
@keyframes cmp-sway {
  0%,100% { transform: rotate(-1deg) translateY(0); }
  50% { transform: rotate(1deg) translateY(-6px); }
}
@keyframes cmp-flicker {
  0%, 19%, 21%, 23%, 25%, 54%, 56%, 100% { opacity: 1; }
  20%, 24%, 55% { opacity: 0.6; }
}
@keyframes cmp-scan { 0% { transform: translateY(-100%); } 100% { transform: translateY(100%); } }
@keyframes cmp-slideUp {
  0% { transform: translateY(18px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}
@keyframes cmp-pulseRing {
  0% { box-shadow: 0 0 0 0 rgba(255,0,127,0.55); }
  100% { box-shadow: 0 0 0 28px rgba(255,0,127,0); }
}

.cmp-root .animate-float { animation: cmp-float 6s ease-in-out infinite; }
.cmp-root .animate-glow { animation: cmp-glow 2.8s ease-in-out infinite; }
.cmp-root .animate-sway { animation: cmp-sway 5s ease-in-out infinite; }
.cmp-root .animate-flicker { animation: cmp-flicker 3.2s linear infinite; }
.cmp-root .animate-scan { animation: cmp-scan 4s linear infinite; }
.cmp-root .animate-slideUp { animation: cmp-slideUp 0.6s cubic-bezier(.2,.8,.2,1) both; }
.cmp-root .animate-pulse-ring { animation: cmp-pulseRing 1.6s cubic-bezier(.2,.8,.2,1) infinite; }
.cmp-root .animate-pulse { animation: pulse 2s cubic-bezier(0.4,0,0.6,1) infinite; }
@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: .5; } }
`;

/* ── Tiny inline icons ── */
const Icon = {
  TikTok: (p) => (
    <svg viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M14 3v9.5a3 3 0 11-3-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M14 3c.5 2.7 2.3 4.5 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
  Facebook: (p) => (
    <svg viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M14 22v-8h3l.5-4H14V7.5c0-1.1.3-2 2-2H18V2.2C17.6 2.1 16.4 2 15 2c-3 0-5 1.8-5 5v3H7v4h3v8h4z" fill="currentColor"/>
    </svg>
  ),
  Mail: (p) => (
    <svg viewBox="0 0 24 24" fill="none" {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M4 7l8 6 8-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
  Link: (p) => (
    <svg viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M10 14a5 5 0 007 0l3-3a5 5 0 00-7-7l-1 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M14 10a5 5 0 00-7 0l-3 3a5 5 0 007 7l1-1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
  Spark: (p) => (
    <svg viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M12 2v6M12 16v6M2 12h6M16 12h6M5 5l4 4M15 15l4 4M5 19l4-4M15 9l4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  ),
  Check: (p) => (
    <svg viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M5 12l4 4L19 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  Fire: (p) => (
    <svg viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M12 22c4 0 7-3 7-7 0-3-2-5-3-7-2 2-3 1-3-2 0-2-1-4-3-4 1 4-5 5-5 11 0 5 3 9 7 9z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
      <path d="M12 18c1.7 0 3-1.3 3-3 0-1.5-1-2-1.5-3-.8 1-1.5.8-1.5-.5-.8.5-2 1.5-2 3.5 0 1.8 1 3 2 3z" fill="currentColor" opacity=".35"/>
    </svg>
  ),
  Trophy: (p) => (
    <svg viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M7 4h10v4a5 5 0 01-10 0V4z" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M4 5h3v3a3 3 0 01-3-3zM20 5h-3v3a3 3 0 003-3z" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M9 18h6v2H9zM10 13v5h4v-5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
    </svg>
  ),
  Clock: (p) => (
    <svg viewBox="0 0 24 24" fill="none" {...p}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
  Arrow: (p) => (
    <svg viewBox="0 0 24 24" fill="none" {...p}>
      <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  Globe: (p) => (
    <svg viewBox="0 0 24 24" fill="none" {...p}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" stroke="currentColor" strokeWidth="1.6"/>
    </svg>
  ),
};

/* ── Reusable ── */
const NeonDivider = () => <div className="hrule" style={{ margin: "3rem 0" }} />;

const SectionLabel = ({ num, children }) => (
  <div style={{ display: "inline-flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.25rem" }}>
    <span className="font-pixel" style={{ fontSize: 10, color: "#00f0ff" }}>/ {num}</span>
    <span style={{ height: 1, width: 40, background: "linear-gradient(90deg, #00f0ff, transparent)" }}></span>
    <span className="font-mono-cmp" style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "#c9c3f0" }}>{children}</span>
  </div>
);

/* ── Language Switcher ── shares state with the global LanguageContext */
const LanguageSwitcher = () => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const current = SUPPORTED_LANGUAGES.find(l => l.code === lang) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    const onDocClick = () => setOpen(false);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open]);

  return (
    <div style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 8, height: 36, padding: "0 12px",
          borderRadius: 8, background: "rgba(11,10,26,0.5)", border: "2px solid rgba(255,0,127,0.7)",
          color: "#fff", fontFamily: '"JetBrains Mono", monospace', fontSize: 11, letterSpacing: ".15em",
          boxShadow: "3px 3px 0 0 #00f0ff", cursor: "pointer",
        }}
      >
        <Icon.Globe style={{ width: 14, height: 14 }} />
        {current.code.toUpperCase()}
      </button>
      {open && (
        <div className="clay-card" style={{ position: "absolute", right: 0, marginTop: 8, width: 180, padding: 8, zIndex: 50 }}>
          {SUPPORTED_LANGUAGES.map(o => (
            <button key={o.code} onClick={() => { setLang(o.code); setOpen(false); }}
              style={{
                width: "100%", textAlign: "left", padding: "8px 12px", borderRadius: 8,
                fontSize: 14, fontFamily: '"JetBrains Mono", monospace',
                display: "flex", alignItems: "center", justifyContent: "space-between",
                color: lang === o.code ? "#ff007f" : "#c9c3f0",
                background: lang === o.code ? "rgba(255,0,127,0.1)" : "transparent",
                border: "none", cursor: "pointer",
              }}>
              <span>{o.label}</span>
              <span style={{ fontSize: 10, color: "#7a7599" }}>{o.code.toUpperCase()}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Top Nav ── */
const Nav = () => {
  const t = useCmpT();
  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 40,
      backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
      background: "rgba(11,10,26,0.05)",
      borderBottom: "1px solid rgba(255,255,255,0.05)",
    }}>
      <div style={{
        maxWidth: 1831, margin: "0 auto", padding: "0 1.25rem",
        height: 60, display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <a href="#top" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none" }}>
          <img src="/campaigns/assets/phuong-logo.png" style={{ height: 36, width: "auto", filter: "drop-shadow(0 0 12px rgba(0,240,255,0.5))" }} alt="Phuong" />
        </a>
        <div style={{ display: "flex", alignItems: "center", gap: 32, fontFamily: '"JetBrains Mono", monospace', fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#c9c3f0" }}
          className="nav-links-desktop">
          <a href="#how" style={{ color: "inherit", textDecoration: "none" }} onMouseEnter={e => e.target.style.color = "#ff007f"} onMouseLeave={e => e.target.style.color = "#c9c3f0"}>{t("nav.howItWorks")}</a>
          <a href="#rules" style={{ color: "inherit", textDecoration: "none" }} onMouseEnter={e => e.target.style.color = "#ff007f"} onMouseLeave={e => e.target.style.color = "#c9c3f0"}>{t("nav.rules")}</a>
          <a href="#rewards" style={{ color: "inherit", textDecoration: "none" }} onMouseEnter={e => e.target.style.color = "#ff007f"} onMouseLeave={e => e.target.style.color = "#c9c3f0"}>{t("nav.rewards")}</a>
          <a href="#submit" style={{ color: "inherit", textDecoration: "none" }} onMouseEnter={e => e.target.style.color = "#ff007f"} onMouseLeave={e => e.target.style.color = "#c9c3f0"}>{t("nav.submit")}</a>
        </div>
        <LanguageSwitcher />
      </div>
    </nav>
  );
};

/* ── iPhone SVG mockup with video ──
   React sometimes fails to reflect `muted` to the DOM attribute, which makes
   Chrome/Safari block autoplay. Set it imperatively via ref and invoke play()
   after mount to guarantee the clip starts. */
const PhoneMockup = ({ src }) => {
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    const tryPlay = () => { const p = v.play(); if (p && p.catch) p.catch(() => {}); };
    tryPlay();
    v.addEventListener("canplay", tryPlay, { once: true });
    return () => v.removeEventListener("canplay", tryPlay);
  }, []);

  return (
    <div style={{ position: "relative", zIndex: 10, margin: "0 auto", width: "100%", aspectRatio: "9/19.5" }}>
      <div style={{ position: "absolute", inset: -8, borderRadius: 48, opacity: 0.6, background: "radial-gradient(ellipse at center, rgba(255,0,127,0.45), transparent 65%)", filter: "blur(24px)" }}></div>
      {/* Video sits ABOVE the frame SVG so the clip is visible; the notch is redrawn on top after. */}
      <div style={{ position: "absolute", overflow: "hidden", zIndex: 11, top: "1.8%", left: "3.2%", right: "3.2%", bottom: "1.8%", borderRadius: 44 }}>
        <video
          ref={videoRef}
          src={src}
          autoPlay
          muted
          defaultMuted
          loop
          playsInline
          preload="auto"
          style={{ width: "100%", height: "100%", objectFit: "cover", background: "#060418", display: "block" }}
        />
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(125deg, rgba(255,255,255,0.08) 0%, transparent 35%, transparent 65%, rgba(0,240,255,0.05) 100%)" }}></div>
      </div>
      <svg viewBox="0 0 360 780" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 10, filter: "drop-shadow(0 30px 50px rgba(0,0,0,0.8))", pointerEvents: "none" }}>
        <defs>
          <linearGradient id="frameG" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#2a2340"/>
            <stop offset="50%" stopColor="#0c0a1c"/>
            <stop offset="100%" stopColor="#2a2340"/>
          </linearGradient>
        </defs>
        <rect x="2" y="2" width="356" height="776" rx="54" fill="url(#frameG)" stroke="#3a3358" strokeWidth="1.2"/>
        <rect x="7" y="7" width="346" height="766" rx="50" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
        <rect x="0" y="150" width="3" height="36" rx="1.5" fill="#15122a"/>
        <rect x="0" y="210" width="3" height="60" rx="1.5" fill="#15122a"/>
        <rect x="0" y="290" width="3" height="60" rx="1.5" fill="#15122a"/>
        <rect x="357" y="180" width="3" height="90" rx="1.5" fill="#15122a"/>
      </svg>
      {/* Notch drawn above the video via its own element so it overlays the clip. */}
      <svg viewBox="0 0 360 780" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 12, pointerEvents: "none" }}>
        <rect x="140" y="22" width="80" height="26" rx="13" fill="#000"/>
        <circle cx="208" cy="35" r="3" fill="#0a0a1a" stroke="#222" strokeWidth="0.5"/>
      </svg>
    </div>
  );
};

/* ── Hero ── */
const Hero = () => {
  const t = useCmpT();
  const [matchLit, setMatchLit] = useState(false);
  useEffect(() => { const tm = setTimeout(() => setMatchLit(true), 600); return () => clearTimeout(tm); }, []);

  const stats = [
    { k: "100+", v: t("hero.statMin"), c: "#ff007f", bread: true },
    { k: "48h", v: t("hero.statPayout"), c: "#00f0ff", bread: false },
    { k: "∞", v: t("hero.statMax"), c: "#a855f7", bread: false },
  ];

  return (
    <section id="top" style={{ position: "relative", overflow: "hidden" }}>
      {/* Desktop-only static background image */}
      <div
        className="hero-static-bg"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          opacity: 0.4,
          backgroundImage: "url(/campaigns/assets/phuong-bg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "hue-rotate(-10deg) saturate(0.8)",
        }}
      ></div>

      {/* Tablet/Mobile-only autoplay background video (replaces the phone on small screens) */}
      <div className="hero-bg-video-wrap" aria-hidden="true">
        <video
          src="https://assets.phuongai.com/campaigns/assets/hero-video.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        />
        <div className="hero-bg-video-overlay"></div>
      </div>

      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          background:
            "radial-gradient(900px 600px at 20% 30%, rgba(255,0,127,0.25), transparent 60%), radial-gradient(800px 500px at 80% 70%, rgba(0,240,255,0.2), transparent 60%)",
          pointerEvents: "none",
        }}
      ></div>

      <div
        style={{
          maxWidth: 1831,
          margin: "0 auto",
          padding: "calc(60px + 2rem) 1.25rem 4rem",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          className="hero-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            gap: "2rem",
            alignItems: "center",
          }}
        >
          {/* Left copy */}
          <div
            className="hero-col-main"
            style={{ gridColumn: "span 7", position: "relative", zIndex: 10 }}
          >
            <div
              className="animate-slideUp"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 24,
                flexWrap: "wrap",
              }}
            >
              <span className="chip">
                <Icon.Spark style={{ width: 14, height: 14 }} />
                {t("hero.rewardsLive")}
              </span>
            </div>

            <h1
              className="animate-slideUp"
              style={{
                animationDelay: ".1s",
                fontWeight: 800,
                lineHeight: 0.95,
                letterSpacing: "-0.02em",
                fontSize: "clamp(2.5rem, 6vw, 5.5rem)",
                margin: 0,
              }}
            >
              <span style={{ display: "block", color: "#f5f3ff" }}>
                {t("hero.h1Line1")}
              </span>
              <span
                className="h-neon animate-glow"
                style={{ display: "block" }}
              >
                {t("hero.h1Matchstick")}
              </span>
              <span style={{ display: "block", color: "#f5f3ff" }}>
                {t("hero.h1Line2Pre")}{" "}
                <em
                  className="text-stroke"
                  style={{ color: "#00f0ff", fontStyle: "italic" }}
                >
                  {t("hero.h1Immortal")}
                </em>{" "}
                {t("hero.h1Line2Post")}
              </span>
            </h1>

            <p
              className="animate-slideUp"
              style={{
                animationDelay: ".2s",
                marginTop: 28,
                maxWidth: "58ch",
                color: "#c9c3f0",
                fontSize: "clamp(1rem, 1.5vw, 1.25rem)",
                lineHeight: 1.7,
              }}
            >
              {t("hero.descPre")}{" "}
              <span style={{ color: "#ff007f", fontWeight: 600 }}>
                {t("hero.descBluebreads")}
              </span>{" "}
              {t("hero.descAnd")}{" "}
              <span style={{ color: "#00f0ff", fontWeight: 600 }}>
                {t("hero.descPro")}
              </span>{" "}
              {t("hero.descTail")}
            </p>

            <div
              className="animate-slideUp"
              style={{
                animationDelay: ".3s",
                marginTop: 36,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 16,
              }}
            >
              <a href="#submit" className="retro-button">
                <Icon.Fire style={{ width: 16, height: 16 }} />
                {t("hero.ctaPrimary")}
              </a>
              <a href="#how" className="retro-button alt">
                {t("hero.ctaSecondary")}
                <Icon.Arrow style={{ width: 12, height: 12 }} />
              </a>
            </div>

            {/* Stats strip */}
            <div
              className="animate-slideUp hero-stats"
              style={{
                animationDelay: ".4s",
                marginTop: 48,
                display: "grid",
                gridTemplateColumns: "repeat(3,1fr)",
                gap: 12,
                maxWidth: 480,
              }}
            >
              {stats.map((s, i) => (
                <div
                  key={i}
                  className="glass-card"
                  style={{ padding: "12px 16px" }}
                >
                  <div
                    className="font-pixel"
                    style={{
                      fontSize: "clamp(1rem, 2vw, 1.5rem)",
                      color: s.c,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {s.bread && (
                      <img
                        src="/campaigns/assets/bluebread.png"
                        alt=""
                        style={{
                          width: 28,
                          height: 28,
                          objectFit: "contain",
                          filter: "drop-shadow(0 0 8px rgba(0,240,255,0.6))",
                        }}
                      />
                    )}
                    {s.k}
                  </div>
                  <div
                    className="font-mono-cmp"
                    style={{
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: "0.15em",
                      color: "#7a7599",
                      marginTop: 6,
                    }}
                  >
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: iPhone mockup */}
          <div
            className="hero-col-phone"
            style={{ gridColumn: "span 5", position: "relative" }}
          >
            <div
              className="hero-stage"
              style={{ position: "relative", margin: "0 auto", maxWidth: 300 }}
            >
              <div
                className="animate-sway"
                style={{
                  position: "absolute",
                  inset: -48,
                  borderRadius: "50%",
                  opacity: 0.6,
                  zIndex: 0,
                  background:
                    "conic-gradient(from 220deg, rgba(255,0,127,.45), rgba(168,85,247,.15), rgba(0,240,255,.4), rgba(255,0,127,.45))",
                  filter: "blur(50px)",
                }}
              ></div>

              <div
                className="animate-float hero-deco-sun"
                style={{
                  position: "absolute",
                  right: -64,
                  top: -40,
                  width: 160,
                  height: 160,
                  zIndex: 0,
                }}
              >
                <svg
                  viewBox="0 0 120 120"
                  style={{ width: "100%", height: "100%" }}
                  className="flame-glow"
                >
                  <defs>
                    <radialGradient id="sunG" cx=".5" cy=".5" r=".5">
                      <stop offset="0%" stopColor="#ffd04d" />
                      <stop offset="55%" stopColor="#ff4d9e" />
                      <stop offset="100%" stopColor="#ff007f" stopOpacity="0" />
                    </radialGradient>
                  </defs>
                  <circle cx="60" cy="60" r="32" fill="url(#sunG)" />
                  {matchLit &&
                    Array.from({ length: 12 }).map((_, i) => {
                      const a = (i / 12) * Math.PI * 2;
                      const x1 = 60 + Math.cos(a) * 36,
                        y1 = 60 + Math.sin(a) * 36;
                      const x2 = 60 + Math.cos(a) * 54,
                        y2 = 60 + Math.sin(a) * 54;
                      return (
                        <line
                          key={i}
                          x1={x1}
                          y1={y1}
                          x2={x2}
                          y2={y2}
                          stroke="#ff007f"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          opacity=".8"
                        />
                      );
                    })}
                </svg>
              </div>

              <PhoneMockup src="https://assets.phuongai.com/campaigns/assets/hero-video.mp4" />

              {/* Floating Bluebread */}
              <div
                className="animate-float hero-deco-bread"
                style={{
                  position: "absolute",
                  left: -56,
                  bottom: 80,
                  width: 128,
                  zIndex: 20,
                  animationDelay: "1.2s",
                }}
              >
                <div style={{ position: "relative" }}>
                  <img
                    src="/campaigns/assets/bluebread.png"
                    style={{
                      width: "100%",
                      height: "auto",
                      filter: "drop-shadow(0 0 24px rgba(0,240,255,0.7))",
                    }}
                    alt=""
                  />
                  <span
                    className="sparkle animate-flicker"
                    style={{ top: 0, right: 8 }}
                  ></span>
                  <span
                    className="sparkle animate-flicker"
                    style={{ bottom: 16, left: 0, animationDelay: ".5s" }}
                  ></span>
                </div>
                <div
                  className="font-mono-cmp"
                  style={{
                    marginTop: 4,
                    textAlign: "center",
                    fontSize: 10,
                    letterSpacing: "0.18em",
                    color: "#00f0ff",
                    textTransform: "uppercase",
                  }}
                >
                  {t("hero.bluebreadDrop")}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div
          style={{
            marginTop: 64,
            position: "relative",
            padding: "16px 0",
            borderTop: "1px solid rgba(255,255,255,0.05)",
            borderBottom: "1px solid rgba(255,255,255,0.05)",
          }}
        >
          <div className="marquee">
            <div
              className="marquee-track font-pixel"
              style={{ fontSize: 14, color: "#c9c3f0" }}
            >
              {Array.from({ length: 2 }).map((_, g) => (
                <span
                  key={g}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 40,
                  }}
                >
                  <span>★ LIGHT THE FUSE ★</span>
                  <span style={{ color: "#ff007f" }}>+100 BLUEBREAD</span>
                  <span>◇ POST · VIRAL · EARN ◇</span>
                  <span style={{ color: "#00f0ff" }}>TIKTOK × FACEBOOK</span>
                  <span>☀ IMMORTAL SUN ☀</span>
                  <span style={{ color: "#a855f7" }}>
                    PRO MEMBERSHIP UNLOCKED
                  </span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── How it Works ── */
const HowItWorks = () => {
  const t = useCmpT();
  const stepIcons = [Icon.Spark, Icon.TikTok, Icon.Fire, Icon.Link, Icon.Trophy];
  const stepTones = ["pink", "cyan", "pink", "cyan", "pink"];
  const rawSteps = t("how.steps") || [];
  const steps = rawSteps.map((s, i) => ({
    n: String(i + 1).padStart(2, "0"),
    title: s.title,
    body: s.body,
    icon: stepIcons[i],
    tone: stepTones[i],
  }));

  return (
    <section id="how" style={{ position: "relative", padding: "5rem 0 7rem" }}>
      <div style={{ maxWidth: 1831, margin: "0 auto", padding: "0 1.25rem" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 24, marginBottom: 56 }}>
          <div>
            <SectionLabel num="02">{t("how.label")}</SectionLabel>
            <h2 style={{ fontSize: "clamp(1.875rem, 4vw, 3.75rem)", fontWeight: 800, letterSpacing: "-0.02em", color: "#f5f3ff", lineHeight: 1.05, maxWidth: "32ch", margin: 0 }}>
              {t("how.headPre")} <span className="h-neon">{t("how.headMid")}</span> {t("how.headTail")}
            </h2>
          </div>
          <p style={{ maxWidth: "28rem", color: "#c9c3f0", fontSize: "1rem" }}>
            {t("how.subcopy")}
          </p>
        </div>

        <div style={{ position: "relative" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20 }}>
            {steps.map((s, i) => (
              <div key={i} className={`glass-card${i % 2 === 1 ? " step-stagger" : ""}`} style={{ padding: "20px 24px", position: "relative", overflow: "hidden", transform: i % 2 === 1 ? "translateY(32px)" : "none" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <span className="step-num">{s.n}</span>
                  <span className="retro-icon-btn" style={s.tone === "cyan" ? { borderColor: "#00f0ff", boxShadow: "4px 4px 0 0 #ff007f" } : {}}>
                    <s.icon style={{ width: 20, height: 20 }} />
                  </span>
                </div>
                <h3 style={{ marginTop: 20, fontSize: "1.25rem", fontWeight: 700, color: "#f5f3ff" }}>{s.title}</h3>
                <p style={{ marginTop: 8, fontSize: "0.875rem", color: "#c9c3f0", lineHeight: 1.6 }}>{s.body}</p>
                <div style={{ marginTop: 20, height: 1, background: "linear-gradient(90deg, rgba(255,0,127,0.4), transparent)" }}></div>
                <div className="font-mono-cmp" style={{ marginTop: 12, fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "#7a7599" }}>
                  {i < steps.length - 1 ? t("how.next") : t("how.done")}
                </div>
                <div style={{ position: "absolute", right: -40, bottom: -40, width: 128, height: 128, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,0,127,.2), transparent 70%)" }}></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Requirements ── */
const RuleRow = ({ icon: I, title, value }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
    <I style={{ width: 16, height: 16, marginTop: 4, color: "#ff007f", flexShrink: 0 }} />
    <div>
      <div className="font-mono-cmp" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.18em", color: "#7a7599" }}>{title}</div>
      <div style={{ marginTop: 2, color: "#f5f3ff", fontSize: "0.875rem" }}>{value}</div>
    </div>
  </div>
);

const Requirements = () => {
  const t = useCmpT();
  return (
  <section id="rules" style={{ position: "relative", padding: "5rem 0 7rem" }}>
    <div style={{ maxWidth: 1831, margin: "0 auto", padding: "0 1.25rem" }}>
      <div style={{ maxWidth: "48rem" }}>
        <SectionLabel num="03">{t("rules.label")}</SectionLabel>
        <h2 style={{ fontSize: "clamp(1.875rem, 4vw, 3.75rem)", fontWeight: 800, letterSpacing: "-0.02em", color: "#f5f3ff", lineHeight: 1.05, margin: 0 }}>
          {t("rules.headPre")} <span className="h-neon">{t("rules.headTail")}</span>
        </h2>
        <p style={{ marginTop: 20, color: "#c9c3f0", fontSize: "1.125rem", maxWidth: "48rem" }}>
          {t("rules.subcopy")}
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24, marginTop: 48 }}>
        {/* TikTok panel */}
        <div className="clay-card" style={{ padding: "24px 32px", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -80, right: -80, width: 240, height: 240, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,0,127,0.35), transparent 65%)" }}></div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span className="retro-icon-btn"><Icon.TikTok style={{ width: 20, height: 20 }} /></span>
              <div>
                <div className="font-mono-cmp" style={{ fontSize: 10, letterSpacing: "0.2em", color: "#7a7599", textTransform: "uppercase" }}>{t("rules.platformA")}</div>
                <h3 className="font-pixel" style={{ fontSize: "0.875rem", color: "#f5f3ff", marginTop: 4 }}>TIKTOK</h3>
              </div>
            </div>
            <span className="chip pink">{t("rules.tiktokThresh")}</span>
          </div>
          <div style={{ marginTop: 24, position: "relative" }}>
            <RuleRow icon={Icon.Check} title={t("rules.originality")} value={t("rules.originalityV")} />
            <RuleRow icon={Icon.Check} title={t("rules.contentRow")} value={t("rules.contentRowV")} />
            <RuleRow icon={Icon.Check} title={t("rules.threshold")} value={t("rules.thresholdV")} />
            <RuleRow icon={Icon.Check} title={t("rules.format")} value={t("rules.formatV")} />
          </div>
          <div style={{ marginTop: 24, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div className="font-mono-cmp" style={{ fontSize: 10, letterSpacing: "0.18em", color: "#7a7599", textTransform: "uppercase" }}>{t("rules.avgPayout")}</div>
            <div className="font-pixel" style={{ color: "#ff007f", fontSize: "0.875rem" }}>{t("rules.tiktokPayout")}</div>
          </div>
        </div>

        {/* Facebook panel */}
        <div className="clay-card" style={{ padding: "24px 32px", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -80, right: -80, width: 240, height: 240, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,240,255,0.35), transparent 65%)" }}></div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span className="retro-icon-btn" style={{ borderColor: "#00f0ff", boxShadow: "4px 4px 0 0 #ff007f" }}><Icon.Facebook style={{ width: 20, height: 20 }} /></span>
              <div>
                <div className="font-mono-cmp" style={{ fontSize: 10, letterSpacing: "0.2em", color: "#7a7599", textTransform: "uppercase" }}>{t("rules.platformB")}</div>
                <h3 className="font-pixel" style={{ fontSize: "0.875rem", color: "#f5f3ff", marginTop: 4 }}>FACEBOOK</h3>
              </div>
            </div>
            <span className="chip">{t("rules.fbThresh")}</span>
          </div>
          <div style={{ marginTop: 24, position: "relative" }}>
            <RuleRow icon={Icon.Check} title={t("rules.fbFormat")} value={t("rules.fbFormatV")} />
            <RuleRow icon={Icon.Check} title={t("rules.placement")} value={t("rules.placementV")} />
            <RuleRow icon={Icon.Check} title={t("rules.engagement")} value={t("rules.engagementV")} />
            <RuleRow icon={Icon.Check} title={t("rules.visibility")} value={t("rules.visibilityV")} />
          </div>
          <div style={{ marginTop: 24, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div className="font-mono-cmp" style={{ fontSize: 10, letterSpacing: "0.18em", color: "#7a7599", textTransform: "uppercase" }}>{t("rules.avgPayout")}</div>
            <div className="font-pixel" style={{ color: "#00f0ff", fontSize: "0.875rem" }}>{t("rules.fbPayout")}</div>
          </div>
        </div>
      </div>

      {/* Submission strip */}
      <div className="glass-card" style={{ marginTop: 24, padding: "20px 24px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span className="retro-icon-btn"><Icon.Link style={{ width: 20, height: 20 }} /></span>
          <div>
            <div className="font-mono-cmp" style={{ fontSize: 10, letterSpacing: "0.2em", color: "#7a7599", textTransform: "uppercase" }}>{t("rules.subMethodLabel")}</div>
            <div style={{ color: "#f5f3ff", fontSize: "1.125rem", marginTop: 4 }}>
              {t("rules.subMethodPre")} <span style={{ color: "#ff007f", fontWeight: 600 }}>{t("rules.subMethodURL")}</span> {t("rules.subMethodMid")}{" "}
              <span style={{ color: "#00f0ff", fontWeight: 600 }}>{t("rules.subMethodEmail")}</span> {t("rules.subMethodTail")}
            </div>
          </div>
        </div>
        <a href="#submit" className="retro-button" style={{ whiteSpace: "nowrap" }}>{t("rules.submitNow")}<Icon.Arrow style={{ width: 12, height: 12 }} /></a>
      </div>
    </div>
  </section>
  );
};

/* ── Rewards ── */
const Rewards = () => {
  const t = useCmpT();
  const [count, setCount] = useState(0);
  const target = 100;
  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (tm) => {
      const p = Math.min(1, (tm - start) / 1200);
      setCount(Math.floor(p * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const tierMeta = [
    { min: "100", max: "200", color: "#f5f3ff", bar: 25 },
    { min: "200", max: "300", color: "#ff007f", bar: 55 },
    { min: "300", max: "1 000", color: "#00f0ff", bar: 82 },
    { min: "1 000", max: "PRO", color: "#a855f7", bar: 100, pro: true },
  ];
  const rawTiers = t("rewards.tiers") || [];
  const tiers = tierMeta.map((m, i) => ({ ...m, label: rawTiers[i]?.label || "", note: rawTiers[i]?.note || "" }));

  return (
    <section id="rewards" style={{ position: "relative", padding: "5rem 0 7rem" }}>
      <div style={{ maxWidth: 1831, margin: "0 auto", padding: "0 1.25rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 40, alignItems: "start" }}>
          <div>
            <SectionLabel num="04">{t("rewards.label")}</SectionLabel>
            <h2 style={{ fontSize: "clamp(1.875rem, 4vw, 3.75rem)", fontWeight: 800, letterSpacing: "-0.02em", color: "#f5f3ff", lineHeight: 1.05, margin: 0 }}>
              <span className="h-neon">{t("rewards.headPre")}</span><br />{t("rewards.headTail")}
            </h2>
            <p style={{ marginTop: 20, color: "#c9c3f0", fontSize: "1.125rem", maxWidth: "28rem" }}>
              {t("rewards.subcopy")}
            </p>

            <div className="glass-card" style={{ position: "relative", marginTop: 40, padding: 32 }}>
              <div className="reward-flex" style={{ display: "flex", alignItems: "center", gap: 24 }}>
                <div style={{ position: "relative", width: 144, height: 144, flexShrink: 0 }}>
                  <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,240,255,0.5), transparent 65%)", filter: "blur(10px)" }}></div>
                  <img src="/campaigns/assets/bluebread.png" className="animate-float" style={{ position: "relative", width: "100%", height: "100%", objectFit: "contain", filter: "drop-shadow(0 0 30px rgba(0,240,255,0.6))" }} alt="Bluebread" />
                </div>
                <div>
                  <div className="font-mono-cmp" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.18em", color: "#7a7599" }}>{t("rewards.minDrop")}</div>
                  <div className="font-pixel" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", color: "#00f0ff", marginTop: 8 }}>{count}+</div>
                  <div style={{ marginTop: 8, color: "#f5f3ff", fontWeight: 600 }}>{t("rewards.guaranteed")}</div>
                  <div style={{ marginTop: 4, fontSize: "0.875rem", color: "#7a7599" }}>{t("rewards.deliveredIn")}</div>
                </div>
              </div>
              <div className="font-mono-cmp" style={{ marginTop: 24, display: "flex", alignItems: "center", gap: 12, fontSize: 12, letterSpacing: "0.15em", color: "#7a7599", textTransform: "uppercase" }}>
                <Icon.Clock style={{ width: 16, height: 16, color: "#ff007f" }} /> {t("rewards.infoStrip")}
              </div>
            </div>
          </div>

          {/* Tier ladder */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {tiers.map((tier, i) => (
              <div key={i} className="clay-card" style={{ padding: "20px 24px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 20 }}>
                <div style={{ minWidth: 140, flexShrink: 0 }}>
                  <div className="font-pixel" style={{ fontSize: 11, color: "#7a7599" }}>{t("rewards.tierLabel")} 0{i + 1}</div>
                  <div className="font-pixel" style={{ fontSize: "1.5rem", marginTop: 8, color: tier.color }}>{tier.label}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                    <span className="font-pixel" style={{ fontSize: "1.25rem", color: "#f5f3ff" }}>{tier.min}</span>
                    <span className="font-mono-cmp" style={{ fontSize: 12, color: "#7a7599" }}>→</span>
                    <span className="font-pixel" style={{ fontSize: "1.25rem", color: "#c9c3f0" }}>{tier.max}</span>
                    <span className="font-mono-cmp" style={{ fontSize: 10, color: "#7a7599", textTransform: "uppercase", letterSpacing: "0.18em" }}>{t("rewards.bluebreadUnit")}</span>
                  </div>
                  <p style={{ marginTop: 8, fontSize: "0.875rem", color: "#c9c3f0" }}>{tier.note}</p>
                  <div style={{ marginTop: 12, height: 6, borderRadius: 999, background: "rgba(255,255,255,0.05)", overflow: "hidden" }}>
                    <div className="fire-bar" style={{ width: `${tier.bar}%` }}></div>
                  </div>
                </div>
                {tier.pro && (
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                    <img src="/campaigns/assets/pro-membership.png" alt="PRO Membership" style={{ width: 80, height: 80, objectFit: "contain", filter: "drop-shadow(0 0 16px rgba(255,200,50,0.6))" }} />
                    <span className="chip pink"><Icon.Trophy style={{ width: 14, height: 14 }} />{t("rewards.proBadge")}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Submit Form ── */
const SubmitForm = () => {
  const t = useCmpT();
  const [platform, setPlatform] = useState("tiktok");
  const [email, setEmail] = useState("");
  const [url, setUrl] = useState("");
  const [agree, setAgree] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const turnstileRef = useRef(null);

  const emailValid = /\S+@\S+\.\S+/.test(email.trim());
  const urlValid = /^https?:\/\//.test(url.trim());
  const valid = emailValid && urlValid && agree;

  const submit = async (e) => {
    e.preventDefault();
    if (status !== "idle") return;

    // Inline validation with translated messages, matching the home page's
    // field-by-field approach rather than a single opaque error.
    if (!emailValid) { setError(t("submit.errInvalidEmail")); return; }
    if (!urlValid)   { setError(t("submit.errInvalidUrl")); return; }
    if (!agree)      { setError(t("submit.errAgree")); return; }

    setError("");

    let captchaToken;
    try {
      captchaToken = await turnstileRef.current?.getResponsePromise();
    } catch {
      captchaToken = null;
    }
    if (!captchaToken) {
      setError(t("submit.errCaptcha"));
      turnstileRef.current?.reset();
      return;
    }

    try {
      setStatus("sending");
      const platformLabel = platform === "tiktok" ? "TikTok" : "Facebook";
      const res = await fetch("https://golden-horseshoe.phuongai.com/feedback/public", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          captcha_token: captchaToken,
          email: email.trim(),
          subject: `[Campaign] ${platformLabel} Post Submission`,
          content: `Platform: ${platformLabel}\nPost URL: ${url.trim()}`,
        }),
      });

      if (!res.ok) throw new Error("submit-failed");

      setStatus("done");
      turnstileRef.current?.reset();
    } catch {
      setError(t("submit.errSubmit"));
      setStatus("idle");
      turnstileRef.current?.reset();
    }
  };

  const bullets = t("submit.bullets") || [];
  const platformOpts = [
    { k: "tiktok", label: "TikTok", icon: Icon.TikTok, min: t("submit.tiktokMin") },
    { k: "facebook", label: "Facebook", icon: Icon.Facebook, min: t("submit.fbMin") },
  ];

  return (
    <section id="submit" style={{ position: "relative", padding: "5rem 0 7rem" }}>
      <div style={{ maxWidth: 1831, margin: "0 auto", padding: "0 1.25rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 40, alignItems: "start" }}>
          <div>
            <SectionLabel num="05">{t("submit.label")}</SectionLabel>
            <h2 style={{ fontSize: "clamp(1.875rem, 4vw, 3.75rem)", fontWeight: 800, letterSpacing: "-0.02em", color: "#f5f3ff", lineHeight: 1.05, margin: 0 }}>
              {t("submit.headPre")} <span className="h-neon">{t("submit.headTail")}</span>
            </h2>
            <p style={{ marginTop: 20, color: "#c9c3f0", fontSize: "1.125rem", maxWidth: "28rem" }}>
              {t("submit.subcopy")}
            </p>
            <ul style={{ marginTop: 32, listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
              {bullets.map((b, i) => (
                <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, fontSize: "0.875rem", color: "#c9c3f0" }}>
                  <Icon.Check style={{ width: 16, height: 16, color: "#00f0ff", marginTop: 2, flexShrink: 0 }} />{b}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="clay-card scanlines submit-card" style={{ padding: "24px 40px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -96, right: -96, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,0,127,0.35), transparent 65%)" }}></div>

              {status === "done" ? (
                <div style={{ position: "relative", padding: "40px 0", textAlign: "center" }}>
                  <div style={{ margin: "0 auto", position: "relative", width: 96, height: 96 }}>
                    <div className="animate-pulse-ring" style={{ position: "absolute", inset: 0, borderRadius: "50%" }}></div>
                    <div style={{ position: "absolute", inset: 0, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,0,127,0.2)", border: "1px solid #ff007f" }}>
                      <Icon.Check style={{ width: 40, height: 40, color: "#ff007f" }} />
                    </div>
                  </div>
                  <h3 style={{ marginTop: 24, fontSize: "1.875rem", fontWeight: 800 }}>{t("submit.doneTitle")}</h3>
                  <p style={{ marginTop: 12, color: "#c9c3f0", maxWidth: "28rem", margin: "12px auto 0" }}>
                    {t("submit.doneDescPre")} <span className="font-mono-cmp" style={{ color: "#00f0ff" }}>{url.slice(0, 40)}{url.length > 40 ? "…" : ""}</span>{t("submit.doneDescMid")}{" "}
                    <span className="font-mono-cmp" style={{ color: "#ff007f" }}>{email}</span> {t("submit.doneDescTail")}
                  </p>
                  <div style={{ marginTop: 24, display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                    <button onClick={() => { setStatus("idle"); setEmail(""); setUrl(""); setAgree(false); setError(""); }} className="retro-button alt">{t("submit.submitAnother")}</button>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} style={{ position: "relative" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <h3 className="font-pixel" style={{ fontSize: "1.125rem", color: "#f5f3ff" }}>{t("submit.cardTitle")}</h3>
                    <div className="font-mono-cmp" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.2em", color: "#7a7599" }}>
                      <span className="animate-pulse" style={{ width: 6, height: 6, borderRadius: "50%", background: "#00f0ff", display: "inline-block" }}></span>
                      {t("submit.secure")}
                    </div>
                  </div>

                  {/* Platform toggle */}
                  <div style={{ marginTop: 24 }}>
                    <div className="font-mono-cmp" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.2em", color: "#7a7599", marginBottom: 8 }}>{t("submit.platformLabel")}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      {platformOpts.map(p => (
                        <button type="button" key={p.k} onClick={() => setPlatform(p.k)}
                          style={{
                            textAlign: "left", padding: 16, borderRadius: 12, cursor: "pointer",
                            display: "flex", alignItems: "center", gap: 12,
                            border: platform === p.k ? "1px solid #ff007f" : "1px solid rgba(255,255,255,0.1)",
                            background: platform === p.k ? "rgba(255,0,127,0.1)" : "rgba(0,0,0,0.2)",
                            boxShadow: platform === p.k ? "0 0 0 4px rgba(255,0,127,0.12)" : "none",
                            transition: "all .15s ease",
                          }}>
                          <span className="retro-icon-btn" style={{ width: 44, height: 44, flexShrink: 0 }}><p.icon style={{ width: 20, height: 20 }} /></span>
                          <div>
                            <div style={{ color: "#f5f3ff", fontWeight: 600 }}>{p.label}</div>
                            <div className="font-mono-cmp" style={{ fontSize: 11, color: "#7a7599", marginTop: 2 }}>min. {p.min}</div>
                          </div>
                          {platform === p.k && <Icon.Check style={{ width: 20, height: 20, color: "#ff007f", marginLeft: "auto" }} />}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Email */}
                  <label style={{ display: "block", marginTop: 24 }}>
                    <span className="font-mono-cmp" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.2em", color: "#7a7599" }}>{t("submit.emailLabel")}</span>
                    <div style={{ position: "relative", marginTop: 8 }}>
                      <Icon.Mail style={{ width: 16, height: 16, position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "#7a7599" }} />
                      <input className="field" type="email" placeholder={t("submit.emailPh")} value={email} onChange={e => setEmail(e.target.value)} maxLength={255} required style={{ paddingLeft: 44 }} />
                    </div>
                  </label>

                  {/* URL */}
                  <label style={{ display: "block", marginTop: 16 }}>
                    <span className="font-mono-cmp" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.2em", color: "#7a7599" }}>{t("submit.urlLabel")}</span>
                    <div style={{ position: "relative", marginTop: 8 }}>
                      <Icon.Link style={{ width: 16, height: 16, position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "#7a7599" }} />
                      <input className="field" type="url"
                        placeholder={platform === "tiktok" ? "https://www.tiktok.com/@you/video/..." : "https://www.facebook.com/groups/.../posts/..."}
                        value={url} onChange={e => setUrl(e.target.value)} maxLength={2000} required style={{ paddingLeft: 44 }} />
                    </div>
                  </label>

                  {/* Agree */}
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 12, marginTop: 20, cursor: "pointer" }}>
                    <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{ marginTop: 4, accentColor: "#ff007f" }} />
                    <span style={{ fontSize: "0.875rem", color: "#c9c3f0" }}>{t("submit.agreeText")}</span>
                  </label>

                  {/* Turnstile CAPTCHA */}
                  <div style={{ marginTop: 24 }}>
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      options={{ theme: "dark", size: "normal" }}
                      onError={(err) => console.error("Captcha verification failed:", err)}
                    />
                  </div>

                  {/* Error */}
                  {error && (
                    <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 10, background: "rgba(255,0,127,0.12)", border: "1px solid rgba(255,0,127,0.4)", color: "#ffc2df", fontSize: "0.875rem" }}>
                      {error}
                    </div>
                  )}

                  {/* Submit */}
                  <div style={{ marginTop: 28, display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", justifyContent: "space-between" }}>
                    <div className="font-mono-cmp" style={{ fontSize: 11, color: "#7a7599", textTransform: "uppercase", letterSpacing: "0.18em" }}>
                      {t("submit.payoutEta")} <span style={{ color: "#00f0ff" }}>{t("submit.payoutEtaSuffix")}</span>
                    </div>
                    <button type="submit" disabled={!valid || status === "sending"} className="retro-button"
                      style={{ opacity: !valid || status === "sending" ? 0.5 : 1, cursor: !valid || status === "sending" ? "not-allowed" : "pointer" }}>
                      {status === "sending" ? t("submit.transmitting") : t("submit.ignite")}
                      <Icon.Fire style={{ width: 16, height: 16 }} />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Final CTA ── */
const FinalCTA = () => {
  const t = useCmpT();
  return (
  <section style={{ position: "relative", padding: "6rem 0 8rem", overflow: "hidden" }}>
    <div style={{ position: "absolute", inset: 0, zIndex: 0, background: "radial-gradient(900px 500px at 50% 50%, rgba(255,0,127,0.3), transparent 60%)" }}></div>
    <div style={{ maxWidth: 1831, margin: "0 auto", padding: "0 1.25rem", position: "relative" }}>
      <div className="glass-panel final-cta-panel" style={{ padding: "32px 64px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -96, left: "50%", transform: "translateX(-50%)", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,0,127,0.25), transparent 65%)" }}></div>
        <div style={{ position: "relative" }}>
          <span className="chip pink"><Icon.Fire style={{ width: 14, height: 14 }} />{t("final.chip")}</span>
          <h2 style={{ marginTop: 32, fontWeight: 800, color: "#f5f3ff", lineHeight: 0.95, letterSpacing: "-0.02em", fontSize: "clamp(2.5rem, 8vw, 6rem)" }}>
            {t("final.h1")}<br />
            <span className="h-neon">{t("final.h2")}</span>
          </h2>
          <p style={{ marginTop: 24, maxWidth: "40rem", margin: "24px auto 0", color: "#c9c3f0", fontSize: "clamp(1rem, 1.5vw, 1.25rem)" }}>
            {t("final.subcopy")}
          </p>
          <div style={{ marginTop: 40, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: 16 }}>
            <a href="#submit" className="retro-button">
              <Icon.Fire style={{ width: 16, height: 16 }} /> {t("final.cta1")}
            </a>
            <a href="#how" className="retro-button alt">{t("final.cta2")}<Icon.Arrow style={{ width: 12, height: 12 }} /></a>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

/* ── Footer ── */
const CampaignFooter = () => {
  const t = useCmpT();
  return (
  <footer style={{ borderTop: "1px solid rgba(255,255,255,0.05)", padding: "40px 0" }}>
    <div style={{ maxWidth: 1831, margin: "0 auto", padding: "0 1.25rem", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <img src="/campaigns/assets/phuong-logo.png" style={{ height: 32, width: "auto", opacity: 0.8 }} alt="Phuong" />
        <span className="font-mono-cmp" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.2em", color: "#7a7599" }}>
          {t("footer.copyright")}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, fontFamily: '"JetBrains Mono", monospace', fontSize: 11, textTransform: "uppercase", letterSpacing: "0.2em", color: "#7a7599" }}>
        <a href="/policy/terms" style={{ color: "inherit", textDecoration: "none" }} onMouseEnter={e => e.target.style.color = "#ff007f"} onMouseLeave={e => e.target.style.color = "#7a7599"}>{t("footer.terms")}</a>
        <a href="/policy/privacy" style={{ color: "inherit", textDecoration: "none" }} onMouseEnter={e => e.target.style.color = "#ff007f"} onMouseLeave={e => e.target.style.color = "#7a7599"}>{t("footer.privacy")}</a>
        <a href="mailto:contact@phuongai.com" style={{ color: "inherit", textDecoration: "none" }} onMouseEnter={e => e.target.style.color = "#ff007f"} onMouseLeave={e => e.target.style.color = "#7a7599"}>{t("footer.support")}</a>
      </div>
    </div>
  </footer>
  );
};

/* ── Page Component ── */
export default function CampaignPostSocialPage() {
  const { lang } = useLanguage();
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "campaign-css";
    style.textContent = CAMPAIGN_CSS;
    document.head.appendChild(style);

    const prevBg = document.body.style.background;
    const prevOverflow = document.body.style.overflowX;
    document.body.style.background = "#06050f";
    document.body.style.overflowX = "hidden";

    return () => {
      document.getElementById("campaign-css")?.remove();
      document.body.style.background = prevBg;
      document.body.style.overflowX = prevOverflow;
    };
  }, []);

  return (
    <div className={`cmp-root${lang === "vi" ? " cmp-lang-vi" : ""}`}>
      <div className="page-bg"></div>
      <Nav />
      <Hero />
      <HowItWorks />
      <Requirements />
      <Rewards />
      <SubmitForm />
      <FinalCTA />
      <CampaignFooter />
    </div>
  );
}
